import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import DataFieldCDS from '@sharedCR/coreDesignSystem/form/DataFieldCDS';
import ExpressionField from 'app/shared/components/form/ExpressionField';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';
import {
  ApplicationImpoundAmountType,
  FieldTypesEnum,
  impoundAmountTypeOptions,
} from 'constants/loanActionEditor';

import { selectLoanActionArithmeticOperationsSchema } from 'store/system/selectors';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { DynamicValueInput } from '../LoanActionItems';

import { getImpoundTypeGroupLabel } from './LoanActionImpoundRow.utils';

const emptyOptions = [];

const LoanActionImpoundRow = ({
  index,
  rootPath,
  isDisabled = false,
  isEditAllowed,
  impoundTypeOptions = emptyOptions,
}) => {
  const { form, validationErrors, handleChange, setInForm, validateField, staticValidateField } =
    useFormContext();
  const arithmeticOperationsSchemaParams = useSelector(selectLoanActionArithmeticOperationsSchema);

  const fieldPath = `${rootPath}[${index}]`;
  const amountTypePath = `${fieldPath}.amountType`;
  const monthlyAmountPath = `${fieldPath}.monthlyAmount`;
  const monthlyAmountExpressionPath = `${fieldPath}.monthlyAmountExpression`;

  const amountType = _.get(form, amountTypePath);
  const isExpressionAmount =
    amountType?.value === ApplicationImpoundAmountType.EXPRESSION_CALCULATED;

  return (
    <Box className={wrapperClassname}>
      <DynamicValueInput
        required
        disabled={isDisabled || !isEditAllowed}
        groupBy={getImpoundTypeGroupLabel}
        label="Impound Type"
        name={`${fieldPath}.impoundType`}
        options={impoundTypeOptions}
        type={FieldTypesEnum.SELECT}
      />
      <DynamicValueInput
        required
        disabled={isDisabled || !isEditAllowed}
        label="Amount Type"
        name={amountTypePath}
        options={impoundAmountTypeOptions}
        type={FieldTypesEnum.SELECT}
      />
      {isExpressionAmount ? (
        <ExpressionField
          required
          dataTestId={loanManagementTestIds.getApplicationImpoundMonthlyAmountExpressionInputId(
            fieldPath,
          )}
          disabled={isDisabled || !isEditAllowed}
          error={!!_.get(validationErrors, monthlyAmountExpressionPath)}
          handleChange={handleChange}
          helperText={_.get(validationErrors, monthlyAmountExpressionPath)}
          isOnlyBooleanAllowed={false}
          label="Monthly Amount Expression"
          name={monthlyAmountExpressionPath}
          schemaParams={arithmeticOperationsSchemaParams}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          value={_.get(form, monthlyAmountExpressionPath) || ''}
        />
      ) : (
        <DataFieldCDS
          required
          dataTestId={loanManagementTestIds.getApplicationImpoundMonthlyAmountInputId(fieldPath)}
          disabled={isDisabled || !isEditAllowed}
          error={!!_.get(validationErrors, monthlyAmountPath)}
          helperText={_.get(validationErrors, monthlyAmountPath)}
          label="Monthly Amount"
          minValue={0}
          name={monthlyAmountPath}
          type={FieldInputType.CURRENCY}
          value={_.get(form, monthlyAmountPath) ?? ''}
          onChange={handleChange}
        />
      )}
    </Box>
  );
};

LoanActionImpoundRow.propTypes = {
  index: PropTypes.number.isRequired,
  rootPath: PropTypes.string.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  impoundTypeOptions: PropTypes.array,
};

export default memo(LoanActionImpoundRow);
